import { useEffect, useState } from 'react';

import { useSkillHistory } from '../../hooks/use-skill-history';
import { useSkillInvocationDetail } from '../../hooks/use-skill-invocation-detail';
import { useSkillInvocations } from '../../hooks/use-skill-invocations';
import { useSkillTree } from '../../hooks/use-skill-tree';
import type { SkillDetailResponse } from '../../lib/api-client';
import { ResizableListPane } from '../../shell/ResizableListPane';

import { SkillExecutionDetail } from './SkillExecutionDetail';
import { SkillExecutionList } from './SkillExecutionList';
import { SkillExecutionPreviewList } from './SkillExecutionPreviewList';
import { SkillFileBrowser } from './SkillFileBrowser';
import { SkillFrontmatter } from './SkillFrontmatter';
import { SkillRevisionList } from './SkillRevisionList';

type Tab = 'details' | 'executions' | 'code';

interface Props {
  skillId: string;
  detail: SkillDetailResponse | null;
  detailStatus: 'idle' | 'loading' | 'success' | 'error';
  detailError?: string | undefined;
}

/**
 * Per-skill detail pane with three tabs:
 *   1. Skill Details          — frontmatter + recent-executions preview.
 *   2. Skill Execution History — full list-detail audit view.
 *   3. Code & Revisions        — file tree + viewer + git revision history.
 *
 * Tab state and the selected execution are owned here so cross-tab
 * navigation (preview-row click → jump to executions tab) stays a pure
 * local concern. Selecting a different skill resets tab + selection.
 */
export function SkillDetail({ skillId, detail, detailStatus, detailError }: Props) {
  const [tab, setTab] = useState<Tab>('details');
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);

  // Reset to the Overview tab + clear execution selection whenever the parent
  // selects a different skill.
  useEffect(() => {
    setTab('details');
    setSelectedExecutionId(null);
  }, [skillId]);

  // Code/Revisions hooks — only fire when the "Code & Revisions" tab is active.
  const history = useSkillHistory(skillId, { enabled: tab === 'code' });
  const tree = useSkillTree(skillId, history.viewedRef, { enabled: tab === 'code' });

  // Execution preview (Details tab) — small page, only fires when the Details
  // tab is mounted to keep the network surface minimal.
  const preview = useSkillInvocations(skillId, { limit: 10, enabled: tab === 'details' });
  // Full execution list (Executions tab) — larger page.
  const fullList = useSkillInvocations(skillId, { limit: 25, enabled: tab === 'executions' });
  const invocationDetail = useSkillInvocationDetail(
    tab === 'executions' ? selectedExecutionId : null,
  );

  // Auto-select the topmost row on first entry to the Executions tab.
  useEffect(() => {
    if (tab !== 'executions') return;
    if (selectedExecutionId) return;
    const first = fullList.invocations[0];
    if (first) setSelectedExecutionId(first.id);
  }, [tab, fullList.invocations, selectedExecutionId]);

  if (detailStatus === 'loading' && !detail) {
    return <Centered>Loading skill…</Centered>;
  }
  if (detailStatus === 'error') {
    return <Centered error>{detailError ?? 'Failed to load skill.'}</Centered>;
  }
  if (!detail) {
    return <Centered>Select a skill to view its details.</Centered>;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: 'var(--bg)',
      }}
    >
      <TabBar
        tab={tab}
        onSelect={(t) => {
          setTab(t);
        }}
      />

      {tab === 'details' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <SkillFrontmatter
            name={detail.name}
            description={detail.description}
            frontmatter={detail.frontmatter}
          />
          <SkillExecutionPreviewList
            invocations={preview.invocations}
            status={preview.status}
            error={preview.error}
            onOpenInExecutionsTab={(id) => {
              setSelectedExecutionId(id);
              setTab('executions');
            }}
          />
        </div>
      )}

      {tab === 'executions' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <ResizableListPane
            storageKey="skill-executions"
            defaultWidth={340}
            minWidth={240}
            maxWidth={560}
            paneStyle={{ borderRight: '1px solid var(--border)' }}
          >
            <SkillExecutionList
              invocations={fullList.invocations}
              status={fullList.status}
              error={fullList.error}
              hasMore={fullList.hasMore}
              onLoadMore={fullList.loadMore}
              selectedId={selectedExecutionId}
              onSelect={setSelectedExecutionId}
            />
          </ResizableListPane>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkillExecutionDetail
              detail={invocationDetail.detail}
              status={invocationDetail.status}
              error={invocationDetail.error}
            />
          </div>
        </div>
      )}

      {tab === 'code' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <SkillFileBrowser
            files={tree.files}
            filesStatus={tree.filesStatus}
            filesError={tree.filesError}
            selectedPath={tree.selectedPath}
            onSelectFile={tree.selectFile}
            fileContent={tree.fileContent}
            fileStatus={tree.fileStatus}
            fileError={tree.fileError}
            isAtRevision={history.viewedRef !== null}
            rootLabel={detail.name}
          />
          <SkillRevisionList
            commits={history.commits}
            status={history.status}
            error={history.error}
            hasMore={history.hasMore}
            onLoadMore={history.loadMore}
            viewedRef={history.viewedRef}
            onViewRef={history.viewAtRef}
          />
        </div>
      )}
    </div>
  );
}

function TabBar({ tab, onSelect }: { tab: Tab; onSelect: (t: Tab) => void }) {
  return (
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
      }}
      role="tablist"
    >
      <TabButton active={tab === 'details'} onClick={() => onSelect('details')}>
        Skill Details
      </TabButton>
      <TabButton active={tab === 'executions'} onClick={() => onSelect('executions')}>
        Skill Execution History
      </TabButton>
      <TabButton active={tab === 'code'} onClick={() => onSelect('code')}>
        Code &amp; Revisions
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: 12,
        fontWeight: 600,
        color: active ? 'var(--accent)' : 'var(--text-mute)',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function Centered({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
